import type { MemberBadge } from "../components/member-badge/MemberBadgeType";

export interface MobileEntranceSlot {
  enabled: boolean;
  sortOrder: number;
  activeIconUrl: string | null;
  deactiveIconUrl: string | null;
}

export interface MobileEntrances {
  diamond: MobileEntranceSlot;
  tabBar: MobileEntranceSlot;
  mine: MobileEntranceSlot;
  messages: MobileEntranceSlot;
}

export interface PcEntrance {
  enabled: boolean;
  sortOrder: number | null;
}

export interface PcMoreServicesEntrance {
  enabled: boolean;
  sortOrder: number;
}

export interface ProductPriceSpec {
  tierId: string;
  enabled: boolean;
  specName: string;
  durationLabel: string;
  priceYuan: number;
}

export type IntelCommentKind = "none" | "ai_recommend" | "manual_comment";

export function normalizeIntelCommentKind(raw: unknown): IntelCommentKind {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (
    value === "ai_recommend" ||
    value === "manual_comment" ||
    value === "none"
  ) {
    return value;
  }
  return "none";
}

export interface StrategySkuOffer {
  subProductItemId: string;
  name: string;
  intro: string;
  priceSpecs: ProductPriceSpec[];
}

export interface StrategyEntitlementItem {
  subProductItemId: string;
  name: string;
  intro: string;
  active: boolean;
  daysRemaining: number | null;
}

export interface SpaceModuleConfig {
  spaceProductId: string;
  productKind: string;
  displayName: string;
  activeIconUrl: string | null;
  deactiveIconUrl: string | null;
  description: string;
  mobileEntrances: MobileEntrances;
  pcEntrance: PcEntrance;
  pcMoreServices?: PcMoreServicesEntrance;
  homeSubscribeCard?: PcMoreServicesEntrance;
  pricingMode: string;
  priceSpecs: ProductPriceSpec[];
  agreementMode: string;
  agreementTemplateId: string;
  mobilePurchasePosterUrl?: string;
  webPurchasePosterUrl?: string;
  productIntroPosterUrl?: string;
  giftTriggerDisplayNames?: string[] | null;
  giftMemberMatchMode?: string | null;
  giftPurchaseHint?: string | null;
  /** 情报产品解读模式；非情报或未返回时为 undefined */
  intelCommentKind?: IntelCommentKind | null;
  /** 问答产品是否允许公开；非问答或未返回时为 undefined */
  qaAllowPublic?: boolean | null;
  strategySaleMode?: "market" | "bundle" | null;
  strategySkuOffers?: StrategySkuOffer[] | null;
  bundledProductDisplayNames?: string[] | null;
  bundledSpaceProductIds?: string[] | null;
  upgradeFromSpaceProductIds?: string[] | null;
}

export type MembershipProduct = SpaceModuleConfig & {
  userSubscribed: boolean;
  daysRemaining: number | null;
  subscribedStrategyCount?: number | null;
  strategyEntitlements?: StrategyEntitlementItem[] | null;
  trialTierVisible?: boolean | null;
  purchaseBlocked?: boolean | null;
  purchaseBlockHint?: string | null;
  upgradeEligible?: boolean | null;
  upgradeSourceSpaceProductId?: string | null;
  upgradeSourceDisplayName?: string | null;
};

export interface SpaceProfile {
  spaceId: string;
  name: string;
  avatarUrl: string;
  followerCount: number;
  subscriptionCount: number;
  certLabelName: string | null;
  signature: string | null;
  douyinId: string | null;
  douyinHomeUrl: string | null;
}

export interface SpaceTabItem {
  key: string;
  label: string;
  activeIconUrl: string | null;
  deactiveIconUrl: string | null;
  requiresLogin: boolean;
  fixed: boolean;
  /** 未读数字角标；优先于 showDot */
  badgeCount?: number;
  /** 无未读时的提醒红点 */
  showDot?: boolean;
}

export interface HomeRotatingAdItem {
  imageUrl: string;
  jumpUrl: string;
}

export type HomeLivePhase = "live" | "upcoming" | "preview";

export interface HomeLiveBroadcast {
  coverUrl: string;
  liveId: string;
  title: string;
  phase: HomeLivePhase;
  viewerCount: number | null;
  reserveCount: number | null;
}

export interface HomeLatestViewpoint {
  imageUrl: string;
  imageUrls?: string[] | null;
  content: string;
  publishTime: string;
  contentAccessible: boolean;
  requiredProductDisplayName?: string | null;
  spaceProductId?: string | null;
  importance?: string | null;
  categoryName?: string | null;
  publisherLabel?: string | null;
  publisherNickname?: string | null;
  publisherAvatarUrl?: string | null;
  title?: string | null;
  titleMode?: boolean | null;
  likeCount?: number | null;
  collectCount?: number | null;
}

export interface HomeCourseItem {
  tutorialId?: string | null;
  coverUrl: string;
  title: string;
  learnerCount: number | null;
  videoUrl?: string | null;
}

export interface GetHomeDataResult {
  rotatingAds: HomeRotatingAdItem[];
  live: HomeLiveBroadcast | null;
  latestViewpoint: HomeLatestViewpoint | null;
  courses: HomeCourseItem[] | null;
}

/** 空间首页本地缓存维度 */
export interface SpaceHomeCacheScope {
  customerKey: string;
  spaceId: string;
}

/** 空间首页本地缓存快照 */
export interface SpaceHomeCacheSnapshot {
  version: number;
  customerKey: string;
  spaceId: string;
  homeData: GetHomeDataResult;
  updatedAt: string;
}

export interface UserSubscriptionItem {
  spaceProductId: string;
  productKind: string;
  displayName: string;
  userSubscribed: boolean;
  daysRemaining: number | null;
  strategySaleMode?: "market" | "bundle" | null;
  subscribedStrategyCount?: number | null;
  strategyEntitlements?: StrategyEntitlementItem[] | null;
  trialTierVisible?: boolean | null;
  purchaseBlocked?: boolean | null;
  purchaseBlockHint?: string | null;
  upgradeEligible?: boolean | null;
  upgradeSourceSpaceProductId?: string | null;
  upgradeSourceDisplayName?: string | null;
}

export interface GetSubscriptionDetailResult {
  items: UserSubscriptionItem[];
  myMemberBadges?: MemberBadge[] | null;
}

export type SpaceFavorState = {
  favorStatus: number;
  subStatus: number;
};
